import {omit} from 'lodash';
import uuid from 'uuid-random';

import {addQueryToPath} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {createObjectPropertyUpdater, createUpdater} from '../../../../srcCommon/helpers/updateHelpers';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {findTimeZone} from '../../../helpers/dateTimeHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiSchedulingLinkDataGroup} from '../../../models/api/dataGroupModel';
import {
  ApiSchedulingLink,
  ApiSchedulingLinkAvailability,
  DraftSchedulingLinkModel,
  SchedulingLinkAvailabilityModel,
  SchedulingLinkModel,
  serializeSchedulingLink,
} from '../../../models/api/schedulingLinkModel';
import {deleteAsync, fetchApiAsync, postApiAsync, putApiAsync} from '../../../services/http/http';
import {findSchedulingLinkLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {
  finishOptimisticUpdateAction,
  optimisticUpdateAsyncAction,
  startOptimisticUpdateAction,
} from '../../optimisticUpdates/optimisticUpdatesActions';
import {OptimisticUpdater} from '../../optimisticUpdates/optimisticUpdatesState';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {DataState} from '../dataState';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Action types.
 */

export enum SchedulingLinkActionsEnum {
  SCHEDULING_LINK_UPDATED = '@@data/schedulingLinks/SCHEDULING_LINK_UPDATED',
  SCHEDULING_LINK_DELETED = '@@data/schedulingLinks/SCHEDULING_LINK_DELETED',
}

/*
 * Types.
 */

export interface SchedulingLinkUpdatedAction extends ActionWithPayload<SchedulingLinkModel> {
  type: SchedulingLinkActionsEnum.SCHEDULING_LINK_UPDATED;
}

export interface SchedulingLinkDeletedAction extends ActionWithPayload<number> {
  type: SchedulingLinkActionsEnum.SCHEDULING_LINK_DELETED;
}

/*
 * Action creators.
 */

export interface FetchSchedulingLinkAvailabilityOptions {
  linkId: number;
  rangeStart: number;
  rangeEnd: number;
}

const SCHEDULING_LINKS = 'scheduling_links';

export function createSchedulingLinkAsyncAction(
  draftLink: DraftSchedulingLinkModel,
): ThunkAsyncAction<SchedulingLinkModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const namespace = getUserNamespace(state);

    const url = `${companyUrl}/namespaces/${namespace}/${SCHEDULING_LINKS}`;
    const schedulingLink = await postApiAsync(ApiSchedulingLink, url, serializeSchedulingLink(draftLink));

    const action: SchedulingLinkUpdatedAction = {
      type: SchedulingLinkActionsEnum.SCHEDULING_LINK_UPDATED,
      payload: schedulingLink,
    };
    dispatch(action);
    return schedulingLink;
  };
}

export function fetchOrCreateInstantReschedulingLinkAsyncAction(calendarVersionId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const namespace = getUserNamespace(state);
    const timezone = findTimeZone();
    const url = `${companyUrl}/namespaces/${namespace}/${SCHEDULING_LINKS}/fetch_create_instant_reschedule_link`;

    const schedulingLink = await postApiAsync(ApiSchedulingLink, url, {
      calendar_version_id: calendarVersionId,
      timezone,
    });

    const action: SchedulingLinkUpdatedAction = {
      type: SchedulingLinkActionsEnum.SCHEDULING_LINK_UPDATED,
      payload: schedulingLink,
    };
    dispatch(action);
  };
}

export function updateSchedulingLinkAsyncAction(
  link: SchedulingLinkModel,
): ThunkAsyncAction<SchedulingLinkModel> {
  const {id: linkId} = link;
  const optimisticUpdater = createOptimisticUpdater(linkId, () => link);

  const optimisticUpdateId = uuid();
  return async (dispatch, getState) => {
    dispatch(startOptimisticUpdateAction(optimisticUpdateId, optimisticUpdater));
    try {
      return findSchedulingLinkLock(linkId).lockAsync(async () => {
        const state = getState();
        const companyUrl = getCompanyUrl(state);

        // Update the scheduling link.
        const url = `${companyUrl}/${SCHEDULING_LINKS}/${linkId}`;
        const schedulingLink = await putApiAsync(ApiSchedulingLink, url, ApiSchedulingLink.serialize(link));

        const action: SchedulingLinkUpdatedAction = {
          type: SchedulingLinkActionsEnum.SCHEDULING_LINK_UPDATED,
          payload: schedulingLink,
        };
        dispatch(finishOptimisticUpdateAction(optimisticUpdateId, action));

        return schedulingLink;
      });
    } catch (error) {
      dispatch(finishOptimisticUpdateAction(optimisticUpdateId));
      throw error;
    }
  };
}

export function deleteSchedulingLinkAsyncAction(linkId: number): ThunkAsyncAction {
  const optimisticUpdater = createOptimisticUpdater(linkId, () => undefined);

  return async (dispatch, getState) => {
    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findSchedulingLinkLock(linkId).lockAsync(async () => {
          const state = getState();
          const companyUrl = getCompanyUrl(state);

          // Delete the scheduling link.
          const url = `${companyUrl}/${SCHEDULING_LINKS}/${linkId}`;
          await deleteAsync(url);

          const action: SchedulingLinkDeletedAction = {
            type: SchedulingLinkActionsEnum.SCHEDULING_LINK_DELETED,
            payload: linkId,
          };

          return action;
        }),
      ),
    );
  };
}

export function fetchSchedulingLinkAsyncAction(linkId: number): ThunkAsyncAction<SchedulingLinkModel> {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const url = `${companyUrl}/${SCHEDULING_LINKS}/${linkId}`;
    const schedulingLink = await fetchApiAsync(ApiSchedulingLink, url);

    const action: SchedulingLinkUpdatedAction = {
      type: SchedulingLinkActionsEnum.SCHEDULING_LINK_UPDATED,
      payload: schedulingLink,
    };
    dispatch(action);

    return schedulingLink;
  };
}

export function fetchSchedulingLinksAsyncAction(cancelToken?: CancelToken): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const namespace = getUserNamespace(state);

    const query = {
      limit: 100,
      sort: '-date',
      is_one_time_link: false,
      is_calendar_event_rescheduling_link: false,
    };

    const url = addQueryToPath(`${companyUrl}/namespaces/${namespace}/${SCHEDULING_LINKS}`, query);
    const schedulingLinks = await fetchApiAsync(ApiSchedulingLinkDataGroup, url, {cancelToken});

    if (
      isDefined(schedulingLinks.limit) &&
      isDefined(schedulingLinks.schedulingLinks) &&
      schedulingLinks.limit === schedulingLinks.schedulingLinks.length
    ) {
      // More scheduling links exist than we are able to fetch at once. We should start to paginate the scheduling links.
      logger.warn('Unable to fetch all scheduling links.');
    }

    dispatch(updateDataAction(schedulingLinks));
  };
}

export function fetchSchedulingLinkAvailabilityAsyncAction(
  options: FetchSchedulingLinkAvailabilityOptions,
): ThunkAsyncAction<SchedulingLinkAvailabilityModel> {
  return async (dispatch, getState) => {
    const {rangeStart, rangeEnd, linkId} = options;

    const state = getState();
    const companyUrl = getCompanyUrl(state);

    const query = {
      range_start: rangeStart,
      range_end: rangeEnd,
    };

    const url = addQueryToPath(`${companyUrl}/${SCHEDULING_LINKS}/${linkId}/availability`, query);
    return await fetchApiAsync(ApiSchedulingLinkAvailability, url);
  };
}

/*
 * Union type.
 */

export type SchedulingLinkAction = SchedulingLinkUpdatedAction | SchedulingLinkDeletedAction;

/*
 * Helpers.
 */

type SchedulingLinkUpdater = (
  previousLink: SchedulingLinkModel | undefined,
) => SchedulingLinkModel | undefined;

function createOptimisticUpdater(linkId: number, updater: SchedulingLinkUpdater): OptimisticUpdater {
  const schedulingLinkUpdater = createUpdater<SchedulingLinkModel | undefined>(updater);

  return createObjectPropertyUpdater<DataState, 'schedulingLinks'>(
    'schedulingLinks',
    createUpdater((schedulingLinks) => {
      const {byId, deletedIds} = schedulingLinks;

      if (deletedIds.contains(linkId)) {
        return schedulingLinks;
      }

      const existingLink = byId[linkId];
      const newLink = schedulingLinkUpdater(existingLink);

      if (!newLink) {
        // Link has been deleted.
        return {
          ...schedulingLinks,
          deletedIds: deletedIds.add(linkId),
          byId: omit(byId, linkId),
        };
      }

      // Link has been updated.
      return {
        ...schedulingLinks,
        byId: {
          ...byId,
          [linkId]: newLink,
        },
      };
    }),
  );
}
